import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import type { RoleDetail } from '@skewb-works/contracts';
import { Check, Plus, ShieldCheck, X } from 'lucide-react';
import { useRoleForm } from '../hooks/useRoleForm';
import { useRoleSubmission } from '../hooks/useRoleSubmission';
import { RoleMatrix } from './RoleMatrix';

interface RoleEditViewProps {
  roleId: number | undefined;
  isEdit: boolean;
  /** The loaded role when editing; undefined when creating. Read once to seed form state. */
  role: RoleDetail | undefined;
  subjects: string[];
}

/**
 * The role create/edit screen: header, save/cancel actions, and the form fields. Mounted by
 * RoleEditPage only once its data is ready, and keyed by role id so navigating to a different
 * role remounts it with fresh initial state.
 */
export function RoleEditView({ roleId, isEdit, role, subjects }: RoleEditViewProps) {
  const form = useRoleForm(role);
  const actions = useRoleSubmission({ roleId, isEdit, values: form.values });

  const mode = isEdit ? 'Edit role' : 'New role';
  const hint = isEdit
    ? 'Edit an existing role and the actions it can perform'
    : 'Define a name and the actions it can perform';

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Role Management"
        subtitle={`${mode} • ${hint}`}
        icon={<ShieldCheck className="size-7 text-primary" />}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={actions.cancel}
              disabled={actions.isSubmitting}
            >
              <X className="w-4 h-4 mr-1.5" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={actions.submit}
              disabled={actions.isSubmitting || !form.canSubmit}
            >
              {isEdit ? <Check className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </>
        }
      />

      <div className="flex-1 overflow-y-auto px-1 py-4">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <Card>
              <CardHeader className="p-4 pb-0">
                <CardTitle className="text-base">Name</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Input
                  id="role-name"
                  aria-label="Name"
                  value={form.name}
                  onChange={(e) => form.setName(e.target.value)}
                  placeholder="e.g. Planner"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-0">
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Textarea
                  id="role-description"
                  aria-label="Description"
                  value={form.description}
                  onChange={(e) => form.setDescription(e.target.value)}
                  placeholder="What this role is for"
                  rows={2}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="role-admin"
                    checked={form.isAdmin}
                    onCheckedChange={(checked) => form.setIsAdmin(checked === true)}
                  />
                  <Label htmlFor="role-admin" className="cursor-pointer font-normal">
                    Full access — grants every action on all subjects
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {form.isAdmin ? null : (
            <Card>
              <CardHeader className="p-4 pb-0">
                <CardTitle className="text-base">Permissions</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <RoleMatrix
                  subjects={subjects}
                  selected={form.selected}
                  lockedKeys={form.lockedKeys}
                  onToggle={form.handleToggle}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
